/**
 * 预约数据处理 Composable
 * 处理预约 API 响应、解析、合并气泡/虚拟数据、写回 appointmentData
 */

import { snakeToCamel } from '@/utils/apiResponse';
import axios from 'axios';
import config from '@/config';
import logger from '@/utils/logger';
import realtimeAPI from '@/api/realtime';

export default function useAppointmentProcessing() {
  return {
    methods: {
      mergeExistingBubbleData(existingData, newData) {
        if (!existingData || !newData || !Array.isArray(existingData) || !Array.isArray(newData)) return;

        // 复用池：少创建、多复用，减少 GC 压力
        if (!this._mergeDecisionsPool) this._mergeDecisionsPool = [];
        if (!this._immediateRecoveriesPool) this._immediateRecoveriesPool = [];
        if (!this._protectionRecoveriesPool) this._protectionRecoveriesPool = [];
        if (!this._zeroChangesMergePool) this._zeroChangesMergePool = [];

        // 遍历新数据
        newData.forEach(newItem => {
          // 关键修复：使用 roomId 进行匹配，因为 roomId 是唯一标识且更可靠
          // roomId 不会在数据刷新时变化，而 roomName 和 patientName 可能会变化
          
          // 关键检查：确保 roomId 的类型和格式一致
          const newRoomId = newItem.roomId ? newItem.roomId.toString() : null;
          const existingItem = existingData.find(item => {
            if (!item.roomId || !newRoomId) return false;
            const existingRoomId = item.roomId.toString();
            return existingRoomId === newRoomId;
          });
          
          if (!existingItem) {
            // 关键修复：强化匹配失败时的waveData保护，确保在整个existingData中彻底搜索
            // 1. 首先尝试精确匹配roomId
            let existingItemForWaveData = existingData.find(item => 
              item.roomId && newItem.roomId && 
              item.roomId.toString() === newItem.roomId.toString()
            );
            
            // 2. 如果第一次匹配失败或匹配项没有waveData，在整个existingData中搜索任何有相同roomId且有有效waveData的项
            if (!existingItemForWaveData || !existingItemForWaveData.waveData || 
                (!existingItemForWaveData.waveData.delta?.length && 
                 !existingItemForWaveData.waveData.theta?.length && 
                 !existingItemForWaveData.waveData.alpha?.length && 
                 !existingItemForWaveData.waveData.beta?.length && 
                 !existingItemForWaveData.waveData.gamma?.length)) {
              existingItemForWaveData = existingData.find(item => {
                if (!item.roomId || !newItem.roomId) return false;
                const match = item.roomId.toString() === newItem.roomId.toString();
                if (!match) return false;
                // 确保有有效的waveData（至少有一个波段有数据）
                return item.waveData && (
                  (item.waveData.delta && item.waveData.delta.length > 0) ||
                  (item.waveData.theta && item.waveData.theta.length > 0) ||
                  (item.waveData.alpha && item.waveData.alpha.length > 0) ||
                  (item.waveData.beta && item.waveData.beta.length > 0) ||
                  (item.waveData.gamma && item.waveData.gamma.length > 0)
                );
              });
            }
            
            if (existingItemForWaveData && existingItemForWaveData.waveData) {
              // 即使匹配失败，也要保留 waveData
              // 如果newItem已经有waveData但为空，或者没有waveData，都使用existingItem的waveData
              const needsRestore = !newItem.waveData || 
                                  (!newItem.waveData.delta?.length && 
                                   !newItem.waveData.theta?.length && 
                                   !newItem.waveData.alpha?.length && 
                                   !newItem.waveData.beta?.length && 
                                   !newItem.waveData.gamma?.length);
              
              if (needsRestore) {
                newItem.waveData = {
                  delta: existingItemForWaveData.waveData.delta ? [...existingItemForWaveData.waveData.delta.map(item => ({ ...item }))] : [],
                  theta: existingItemForWaveData.waveData.theta ? [...existingItemForWaveData.waveData.theta.map(item => ({ ...item }))] : [],
                  alpha: existingItemForWaveData.waveData.alpha ? [...existingItemForWaveData.waveData.alpha.map(item => ({ ...item }))] : [],
                  beta: existingItemForWaveData.waveData.beta ? [...existingItemForWaveData.waveData.beta.map(item => ({ ...item }))] : [],
                  gamma: existingItemForWaveData.waveData.gamma ? [...existingItemForWaveData.waveData.gamma.map(item => ({ ...item }))] : []
                };
              }
            }

            // 匹配失败时的保护机制：如果新数据无效，尝试从 lastValidBrainwaveData 恢复
            const treatmentStatus = this.getTreatmentStatus(newItem);
            const isTreatmentEnded = treatmentStatus === '已结束';
            
            // 检查新数据是否有效
            const newDataIsValid = (newItem.delta > 0 || newItem.theta > 0 || 
                                   newItem.alpha > 0 || newItem.beta > 0 || newItem.gamma > 0);
            
            // 尝试从 lastValidBrainwaveData 恢复（注意：这里需要从 existingData 中查找对应的 lastValidBrainwaveData）
            const existingItemForRecovery = existingItemForWaveData || existingData.find(item => 
              item.roomId && newItem.roomId && 
              item.roomId.toString() === newItem.roomId.toString()
            );
            
            if (!newDataIsValid && !isTreatmentEnded && existingItemForRecovery) {
              // 新数据无效且治疗未结束，尝试从现有数据恢复
              const hasLastValid = existingItemForRecovery.lastValidBrainwaveData && 
                (existingItemForRecovery.lastValidBrainwaveData.delta > 0 || 
                 existingItemForRecovery.lastValidBrainwaveData.theta > 0 ||
                 existingItemForRecovery.lastValidBrainwaveData.alpha > 0 ||
                 existingItemForRecovery.lastValidBrainwaveData.beta > 0 ||
                 existingItemForRecovery.lastValidBrainwaveData.gamma > 0);
              
              if (hasLastValid) {
                // 从 lastValidBrainwaveData 恢复
                const recovered = existingItemForRecovery.lastValidBrainwaveData;
                newItem.delta = recovered.delta;
                newItem.theta = recovered.theta;
                newItem.alpha = recovered.alpha;
                newItem.beta = recovered.beta;
                newItem.gamma = recovered.gamma;
                newItem.lastValidBrainwaveData = { ...recovered };
                
              } else {
                // 尝试从现有数据的直接值恢复
                const hasExistingValid = (existingItemForRecovery.delta > 0 || 
                                         existingItemForRecovery.theta > 0 ||
                                         existingItemForRecovery.alpha > 0 ||
                                         existingItemForRecovery.beta > 0 ||
                                         existingItemForRecovery.gamma > 0);
                
                if (hasExistingValid) {
                  newItem.delta = existingItemForRecovery.delta;
                  newItem.theta = existingItemForRecovery.theta;
                  newItem.alpha = existingItemForRecovery.alpha;
                  newItem.beta = existingItemForRecovery.beta;
                  newItem.gamma = existingItemForRecovery.gamma;
                  
                }
              }
            }
            
            //   }))
          } else {
            // 匹配成功，记录 existingItem 的详细状态
          }
          
          // 如果找到匹配项，则合并脑波数据和有效气泡
          if (existingItem) {
            // 优化：保留有效气泡，只清理过期气泡
            if (existingItem.bubbles && Array.isArray(existingItem.bubbles) && existingItem.bubbles.length > 0) {
              // 过滤出有效的气泡（未过期且可见）
              const validBubbles = this.filterValidBubbles(existingItem.bubbles);
              // 如果有有效气泡，保留它们；否则设置为空数组
              newItem.bubbles = validBubbles.length > 0 ? validBubbles : [];
              // 如果有有效气泡，不需要重置lastBubbleTime，避免立即重新生成
              if (validBubbles.length === 0) {
                newItem.lastBubbleTime = 0;
              } else {
                // 保留原有的lastBubbleTime，避免重复生成气泡
                newItem.lastBubbleTime = existingItem.lastBubbleTime || 0;
              }
            } else {
              // 如果没有气泡，设置为空数组并重置时间
              newItem.bubbles = [];
              newItem.lastBubbleTime = 0;
            }
  
            // 关键修复：合并历史脑波数据 - 确保 waveData 始终被保留
            // 即使在初始化时已经复制了，这里也要确保合并逻辑正确执行
            if (existingItem.waveData) {
              // 如果新数据没有waveData，直接使用旧数据（深拷贝）
              if (!newItem.waveData || 
                  !newItem.waveData.delta || !Array.isArray(newItem.waveData.delta) ||
                  (newItem.waveData.delta.length === 0 && existingItem.waveData.delta && existingItem.waveData.delta.length > 0)) {
                // 深拷贝旧数据，避免引用问题
                newItem.waveData = {
                  delta: existingItem.waveData.delta ? [...existingItem.waveData.delta.map(item => ({ ...item }))] : [],
                  theta: existingItem.waveData.theta ? [...existingItem.waveData.theta.map(item => ({ ...item }))] : [],
                  alpha: existingItem.waveData.alpha ? [...existingItem.waveData.alpha.map(item => ({ ...item }))] : [],
                  beta: existingItem.waveData.beta ? [...existingItem.waveData.beta.map(item => ({ ...item }))] : [],
                  gamma: existingItem.waveData.gamma ? [...existingItem.waveData.gamma.map(item => ({ ...item }))] : []
                };
              } else {
                // 如果新数据有waveData，合并历史数据
                const waveTypes = ['delta', 'theta', 'alpha', 'beta', 'gamma'];
                waveTypes.forEach(type => {
                  if (existingItem.waveData[type] && Array.isArray(existingItem.waveData[type]) && existingItem.waveData[type].length > 0) {
                    if (!newItem.waveData[type] || !Array.isArray(newItem.waveData[type])) {
                      newItem.waveData[type] = [];
                    }
                    // 将旧数据追加到新数据前面，保持历史连续性
                    // 使用深拷贝避免引用问题
                    // 关键：确保不丢失旧数据，即使新数据有数据，也要合并
                    const existingLength = existingItem.waveData[type].length;
                    const newLength = newItem.waveData[type].length;
                    
                    // 合并时，优先保留旧数据，然后将新数据追加
                    newItem.waveData[type] = [
                      ...existingItem.waveData[type].map(item => ({ ...item })),
                      ...newItem.waveData[type].map(item => ({ ...item }))
                    ];
                    // 限制历史数据长度，避免内存溢出（保留最新的1000个数据点）
                    if (newItem.waveData[type].length > 1000) {
                      newItem.waveData[type] = newItem.waveData[type].slice(-1000);
                    }
                    
                    if (existingLength > 0 || newLength > 0) {
                    }
                  }
                });
              }
            } else if (!newItem.waveData) {
              // 如果旧数据也没有 waveData，初始化空数组
              newItem.waveData = {
                delta: [],
                theta: [],
                alpha: [],
                beta: [],
                gamma: []
              };
            }
            
            // 关键修复：合并脑波值，更严格地保护现有有效值
            // 只有当新数据明确提供了非0的有效值时才更新，否则保留旧数据
            const treatmentStatus = this.getTreatmentStatus(newItem);
            const isTreatmentEnded = treatmentStatus === '已结束';
            
            // 关键调试：记录合并前的数据状态
            const mergeBeforeState = {
              roomId: newItem.roomId,
              newValues: {
                delta: newItem.delta,
                theta: newItem.theta,
                alpha: newItem.alpha,
                beta: newItem.beta,
                gamma: newItem.gamma
              },
              existingValues: {
                delta: existingItem.delta,
                theta: existingItem.theta,
                alpha: existingItem.alpha,
                beta: existingItem.beta,
                gamma: existingItem.gamma
              },
              isTreatmentEnded
            };
            
            // 定义脑波值数组（使用复用池，避免每次新建数组）
            const brainwaveBands = ['delta', 'theta', 'alpha', 'beta', 'gamma'];
            this._mergeDecisionsPool.length = 0;
            const mergeDecisions = this._mergeDecisionsPool;

            if (!isTreatmentEnded) {
              // 关键修复：简化合并逻辑，使用最简单直接的规则
              // 规则：如果新数据无效（0或undefined）且旧数据有效（非0且非undefined），直接使用旧数据
              // 记录合并前的初始状态
              const mergeInitialState = {
                roomId: newItem.roomId,
                bands: brainwaveBands.map(band => ({
                  band,
                  newValue: newItem[band],
                  existingValue: existingItem[band]
                }))
              };
              
              brainwaveBands.forEach(band => {
                const newValue = newItem[band];
                const existingValue = existingItem[band];
                const beforeValue = newItem[band];
                let decision = '';
                let finalValue = newItem[band];
                
                // 简化的合并逻辑：使用最简单直接的规则
                const existingIsValid = existingValue !== undefined && existingValue !== null && existingValue !== 0;
                const newIsInvalid = newValue === undefined || newValue === null || newValue === 0;
                const newIsValid = newValue !== undefined && newValue !== null && newValue > 0;
                
                if (existingIsValid && newIsInvalid) {
                  // 规则1：旧值有效且新值无效，直接使用旧值（最高优先级）
                  newItem[band] = existingValue;
                  decision = '使用旧值（旧值有效，新值无效）';
                  finalValue = existingValue;
                } else if (newIsValid) {
                  // 规则2：新值有效，使用新值
                  newItem[band] = newValue;
                  decision = '使用新值（新值有效）';
                  finalValue = newValue;
                } else if (existingValue !== undefined && existingValue !== null) {
                  // 规则3：旧值存在（即使是0），优先使用旧值
                  newItem[band] = existingValue;
                  decision = '使用旧值（旧值存在）';
                  finalValue = existingValue;
                } else if (newValue !== undefined && newValue !== null) {
                  // 规则4：新值存在（即使是0），使用新值
                  newItem[band] = newValue;
                  decision = '使用新值（新值存在）';
                  finalValue = newValue;
                } else {
                  // 规则5：双方都为undefined，保持undefined
                  decision = '保持undefined（双方都undefined）';
                  finalValue = undefined;
                }
                
                // 关键修复：becameZero 应该基于 existingValue（旧数据的值），而不是 beforeValue（新数据的初始值）
                // 这样才能正确检测到：旧数据有有效值，但合并后变成了0或undefined
                const becameZero = (existingValue !== undefined && existingValue !== null && existingValue !== 0) && 
                                   (finalValue === undefined || finalValue === null || finalValue === 0);
                
                mergeDecisions.push({
                  band,
                  newValue,
                  existingValue,
                  beforeValue,
                  afterValue: finalValue,
                  decision,
                  changed: beforeValue !== finalValue,
                  becameZero
                });
              });
              
              // 关键调试：记录合并决策，特别是变成0的情况
              // 记录合并后的最终状态
              const mergeFinalState = {
                roomId: newItem.roomId,
                bands: brainwaveBands.map(band => ({
                  band,
                  finalValue: newItem[band]
                }))
              };
              
              this._zeroChangesMergePool.length = 0;
              mergeDecisions
                .filter(d => d.becameZero)
                .forEach(d => this._zeroChangesMergePool.push(d));
              const zeroChanges = this._zeroChangesMergePool;
              const hasChanges = mergeDecisions.some(d => d.changed);
              
              // 关键修复：如果检测到 becameZero，立即恢复值
              if (zeroChanges.length > 0) {
                this._immediateRecoveriesPool.length = 0;
                const immediateRecoveries = this._immediateRecoveriesPool;
                zeroChanges.forEach(zeroChange => {
                  // 立即从 existingValue 恢复，因为 existingValue 是合并前的旧值
                  if (zeroChange.existingValue !== undefined && zeroChange.existingValue !== null && zeroChange.existingValue !== 0) {
                    const beforeRecovery = newItem[zeroChange.band];
                    newItem[zeroChange.band] = zeroChange.existingValue;
                    immediateRecoveries.push({
                      band: zeroChange.band,
                      beforeRecovery: beforeRecovery,
                      afterRecovery: zeroChange.existingValue,
                      source: 'existingValue'
                    });
                  }
                });
                
                //   })),
                //   }))
              } else if (hasChanges) {
                // 有变化但没有变成0，记录详细信息
                //   }))
              } else {
                // 没有变化，记录详细状态以便调试
                //   }))
              }
              
              // 如果新数据没有 lastValidBrainwaveData，但旧数据有，保留它
              if (!newItem.lastValidBrainwaveData && existingItem.lastValidBrainwaveData) {
                newItem.lastValidBrainwaveData = { ...existingItem.lastValidBrainwaveData };
              }
              
              // 如果新数据没有 lastReceiveTime，但旧数据有，保留它
              if (!newItem.lastReceiveTime && existingItem.lastReceiveTime) {
                newItem.lastReceiveTime = existingItem.lastReceiveTime;
              }
              
              // 如果新数据没有 currentDisplayIndex，但旧数据有，保留它
              if (existingItem.currentDisplayIndex !== undefined) {
                newItem.currentDisplayIndex = existingItem.currentDisplayIndex;
              }
              if (existingItem.lastDisplayUpdateTime !== undefined) {
                newItem.lastDisplayUpdateTime = existingItem.lastDisplayUpdateTime;
              }
              
              // 如果新数据没有 realtimeDataArray，但旧数据有，保留它（关键：用于每秒切换显示）
              if (existingItem.realtimeDataArray && Array.isArray(existingItem.realtimeDataArray) && existingItem.realtimeDataArray.length > 0) {
                // 优先保留旧数据的 realtimeDataArray，因为它包含了历史数据
                if (!newItem.realtimeDataArray || !Array.isArray(newItem.realtimeDataArray) || newItem.realtimeDataArray.length === 0) {
                  // 深拷贝数组，避免引用问题
                  newItem.realtimeDataArray = existingItem.realtimeDataArray.map(item => ({ ...item }));
                }
              }
              
              // #region agent log
  
              // #endregion
              
              // 关键修复：合并后的最终保护检查
              // 如果合并后的值是0或undefined，但旧值有效，强制恢复旧值
              // 这是一个双重保险，确保即使合并逻辑有问题，也能在这里恢复
              if (!isTreatmentEnded) {
                // 记录保护检查前的状态
                const beforeProtectionState = brainwaveBands.map(band => ({
                  band,
                  mergedValue: newItem[band],
                  existingValue: existingItem[band]
                }));
                
                this._protectionRecoveriesPool.length = 0;
                const protectionRecoveries = this._protectionRecoveriesPool;
                brainwaveBands.forEach(band => {
                  const mergedValue = newItem[band];
                  const existingValue = existingItem[band];
                  
                  // 检查合并后的值是否无效（0或undefined），但旧值有效
                  const mergedIsInvalid = mergedValue === undefined || mergedValue === null || mergedValue === 0;
                  const existingIsValid = existingValue !== undefined && existingValue !== null && existingValue !== 0;
                  
                  if (mergedIsInvalid && existingIsValid) {
                    // 强制恢复旧值
                    const beforeRecovery = mergedValue;
                    newItem[band] = existingValue;
                    protectionRecoveries.push({
                      band,
                      beforeRecovery,
                      afterRecovery: existingValue,
                      reason: '合并后值无效但旧值有效，强制恢复'
                    });
                  } else if (mergedIsInvalid && !existingIsValid) {
                    // 如果合并后的值无效，且旧值也无效，尝试从 lastValidBrainwaveData 恢复
                    if (existingItem.lastValidBrainwaveData) {
                      const lastValidValue = existingItem.lastValidBrainwaveData[band];
                      if (lastValidValue !== undefined && lastValidValue !== null && lastValidValue !== 0) {
                        const beforeRecovery = mergedValue;
                        newItem[band] = lastValidValue;
                        protectionRecoveries.push({
                          band,
                          beforeRecovery,
                          afterRecovery: lastValidValue,
                          reason: '合并后值无效且旧值也无效，从lastValidBrainwaveData恢复'
                        });
                      }
                    }
                  }
                });
                
                // 记录保护检查后的状态
                const afterProtectionState = brainwaveBands.map(band => ({
                  band,
                  finalValue: newItem[band]
                }));
                
                // 记录保护检查的详细日志
                if (protectionRecoveries.length > 0) {
                  //   }))
                } else {
                  // 即使没有恢复，也记录检查结果，以便调试
                  //   afterProtectionState
                }
              }
            } else {
              // 治疗已结束，允许更新为0
              // 但仍然优先使用新数据中的有效值
              brainwaveBands.forEach(band => {
                if (newItem[band] !== undefined && newItem[band] !== null) {
                  newItem[band] = newItem[band];
                } else if (existingItem[band] !== undefined) {
                  newItem[band] = existingItem[band];
                }
              });
            }
  
            // 合并元气分数相关字段
            if (existingItem.energyScore !== undefined) {
              // 保留历史累积的元气分数
              newItem.energyScore = existingItem.energyScore;
            }
  
            if (existingItem.lastCalculatedSecond !== undefined) {
              // 保留上次计算到的秒数
              newItem.lastCalculatedSecond = existingItem.lastCalculatedSecond;
            }
  
            if (existingItem.finalPercentage !== undefined) {
              // 保留最终进度百分比
              newItem.finalPercentage = existingItem.finalPercentage;
            }
  
            if (existingItem.totalSeconds !== undefined) {
              // 保留总秒数
              newItem.totalSeconds = existingItem.totalSeconds;
            }
          }
        });
      },
      
      /**
       * 获取当日预约记录数据
       */
      async fetchTodayAppointmentData() {
        try {
          const storeId = this.effectiveStoreId;
          if (!storeId) {
            logger.warn('⚠️ 门店ID未设置，无法获取当日预约记录');
            return;
          }
          const todayUrl = config.getApiUrl(config.apiEndpoints.appointments.today, storeId);
          const response = await realtimeAPI.getTodayAppointments(storeId);
          if (response.success) {
            this.todayAppointmentData = Array.isArray(response.data) ? response.data : 
                                       (response.data.appointments ? response.data.appointments : []);

            if (response.message) {
              // 状态码1001的情况：请求成功但无数据
            } else {
            }
          } else {
            this.todayAppointmentData = [];
          }
          
        } catch (error) {
          this.todayAppointmentData = [];
        }
      },
      
      /**
       * 计算当日元气恢复人数
       * @returns {number} 当日元气恢复总人数
       */
      // 恢复计数相关方法已提取到 useRecoveryCount composable
      
      async fetchAppointmentData() {
        logger.debug('[预约] fetchAppointmentData 被调用', {
          isSliding: this.isSliding,
          isRotating: this.isRotating,
          lastRotationEndTime: this.lastRotationEndTime
        });
        // 关键修复：滑动期间和轮播刚结束时完全跳过数据刷新，避免重置脑波数据
        // 轮播刚结束时，数据可能还在稳定中，延迟刷新避免数据丢失
        if (this.isSliding || this.isRotating) {
          if (this.fetchAppointmentDataRetryTimer) {
            clearTimeout(this.fetchAppointmentDataRetryTimer);
            this.fetchAppointmentDataRetryTimer = null;
          }
          this.fetchAppointmentDataRetryTimer = setTimeout(() => {
            this.fetchAppointmentDataRetryTimer = null;
            if (!this.isSliding && !this.isRotating) {
              if (this.fetchAppointmentDataRetryTimer2) {
                clearTimeout(this.fetchAppointmentDataRetryTimer2);
                this.fetchAppointmentDataRetryTimer2 = null;
              }
              this.fetchAppointmentDataRetryTimer2 = setTimeout(() => {
                this.fetchAppointmentDataRetryTimer2 = null;
                if (!this.isSliding && !this.isRotating) {
                  this.fetchAppointmentData();
                }
              }, 500);
            }
          }, 1000);
          logger.debug(`[数据刷新] 跳过刷新：轮播中 (isSliding: ${this.isSliding}, isRotating: ${this.isRotating})`);
          return;
        }
        
        // 检查是否在轮播完成后很短时间内（1.5秒内），如果是，延迟刷新
        const now = Date.now();
        if (this.lastRotationEndTime && (now - this.lastRotationEndTime) < 1500) {
          const remainingDelay = 1500 - (now - this.lastRotationEndTime);
          logger.debug(`[数据刷新] 延迟刷新：轮播刚结束，延迟 ${remainingDelay}ms`);
          if (this.fetchAppointmentDataDelayTimer) {
            clearTimeout(this.fetchAppointmentDataDelayTimer);
            this.fetchAppointmentDataDelayTimer = null;
          }
          this.fetchAppointmentDataDelayTimer = setTimeout(() => {
            this.fetchAppointmentDataDelayTimer = null;
            if (!this.isSliding && !this.isRotating) {
              this.fetchAppointmentData();
            }
          }, remainingDelay);
          return;
        }
        try {
          // 关键修复：在调用API之前，保存受保护的虚拟数据（治疗时间未结束的虚拟数据）
          const protectedVirtualData = [];
          if (this.appointmentData && this.appointmentData.length > 0) {
            this.appointmentData.forEach(appointment => {
              // 检查是否为虚拟数据
              const isVirtualData = appointment.isMock || appointment.dataType === 1 || !appointment.roomId;
              
              if (isVirtualData) {
                const treatmentStatus = this.getTreatmentStatus(appointment);
                
                // 如果治疗时间未结束，保存为受保护数据
                if (treatmentStatus === '治疗中' || treatmentStatus === '治疗未开始') {
                  protectedVirtualData.push({
                    ...appointment,
                    // 深拷贝关键字段，避免引用问题
                    waveData: appointment.waveData ? {
                      delta: appointment.waveData.delta ? [...appointment.waveData.delta] : [],
                      theta: appointment.waveData.theta ? [...appointment.waveData.theta] : [],
                      alpha: appointment.waveData.alpha ? [...appointment.waveData.alpha] : [],
                      beta: appointment.waveData.beta ? [...appointment.waveData.beta] : [],
                      gamma: appointment.waveData.gamma ? [...appointment.waveData.gamma] : []
                    } : null
                  });
                  
                }
              }
            });
          }
          
          const storeId = this.effectiveStoreId;
          // 构建API URL，将门店ID作为路径参数传递给后端
          const apiUrl = config.getApiUrl(
            config.apiEndpoints.appointments.latestByRoom,
            storeId
          );
          
          logger.debug('=============================');
          logger.debug('[预约] 即将请求预约数据', apiUrl);
          
          const response = await axios.get(apiUrl);
          
          // 处理API响应，并传入受保护的虚拟数据
          this.handleAppointmentDataResponse(response, apiUrl, protectedVirtualData);
          
        } catch (error) {
          // 友好的异常处理
          this.handleAppointmentDataError(error);
        }
      },
  
      /**
       * 处理预约数据API响应
       * @param {Object} response - API响应对象
       * @param {string} apiUrl - 请求的API地址
       * @param {Array} protectedVirtualData - 受保护的虚拟数据（治疗时间未结束的虚拟数据）
       */
      handleAppointmentDataResponse(response, apiUrl, protectedVirtualData = []) {
        try {
          
          // 灵活处理不同的数据格式
          let appointmentArray = this.parseAppointmentData(response.data);
          
          if (appointmentArray === null) {
            logger.debug(`[数据赋值] 清空appointmentData: 数据格式无法识别`);
            
            // 关键修复：即使API返回空数据，也要保留受保护的虚拟数据
            if (protectedVirtualData && protectedVirtualData.length > 0) {
              this.appointmentData = [...protectedVirtualData];
            } else {
              this.appointmentData = [];
            }
            
            this.addSystemLog('预约数据格式无法识别，请检查API返回格式');
            return;
          }
          
          // 详细检查每个预约的数据结构

          // 处理预约数据
          this.processAppointmentData(appointmentArray, protectedVirtualData);
          
        } catch (error) {
          logger.debug(`[数据赋值] 清空appointmentData: 处理响应异常 - ${error.message || '未知错误'}`);
          
          // 关键修复：即使处理异常，也要保留受保护的虚拟数据
          if (protectedVirtualData && protectedVirtualData.length > 0) {
            this.appointmentData = [...protectedVirtualData];
          } else {
            this.appointmentData = [];
          }
          
          this.addSystemLog('处理预约数据响应异常: ' + (error.message || '未知错误'));
        }
      },
  
      /**
       * 解析预约数据，支持多种数据格式
       * @param {*} data - API返回的原始数据
       * @returns {Array|null} 解析后的预约数组，解析失败返回null
       */
      parseAppointmentData(data) {
        try {
          let appointmentArray = null;
          
          if (Array.isArray(data)) {
            // 如果返回的是数组，直接使用
            appointmentArray = data;
          } else if (data && typeof data === 'object') {
            // 处理 ApiResponse 格式：{ code: 200, message: "...", data: [...] }
            if (data.code !== undefined && data.data !== undefined) {
              // ApiResponse 格式
              if (Array.isArray(data.data)) {
                appointmentArray = data.data;
              } else {
                appointmentArray = null;
              }
            } else if (data.data && Array.isArray(data.data)) {
              // 标准的 { data: [...] } 格式
              appointmentArray = data.data;
            } else if (data.appointments && Array.isArray(data.appointments)) {
              // { appointments: [...] } 格式
              appointmentArray = data.appointments;
            } else if (data.list && Array.isArray(data.list)) {
              // { list: [...] } 格式
              appointmentArray = data.list;
            } else {
              // 如果对象中没有找到数组字段，将整个对象作为单个元素的数组
              appointmentArray = [data];
            }
          } else {
            return null;
          }
          
          // 将下划线字段名转换为驼峰命名
          if (appointmentArray && Array.isArray(appointmentArray)) {
            return appointmentArray.map(item => snakeToCamel(item));
          }
          
          return null;
        } catch (error) {
          return null;
        }
      },
  
      /**
       * 处理预约数据获取过程中的异常
       * @param {Error} error - 错误对象
       */
      handleAppointmentDataError(error) {
        
        // 根据错误类型提供友好的提示
        let friendlyMessage = '预约数据获取异常';
        
        if (error.code === 'ECONNREFUSED') {
          friendlyMessage = '预约数据服务连接被拒绝，可能服务未启动';
        } else if (error.code === 'ENOTFOUND') {
          friendlyMessage = '无法解析预约数据服务器地址';
        } else if (error.code === 'ETIMEDOUT') {
          friendlyMessage = '预约数据请求超时，服务器响应缓慢';
        } else if (error.response) {
          const status = error.response.status;
          if (status === 404) {
            friendlyMessage = '预约数据接口不存在';
          } else if (status === 500) {
            friendlyMessage = '预约数据服务器内部错误';
          } else if (status >= 400 && status < 500) {
            friendlyMessage = '预约数据请求参数错误';
          } else {
            friendlyMessage = `预约数据服务响应异常 (${status})`;
          }
        } else if (error.message) {
          friendlyMessage = error.message;
        }
        
        logger.warn('[预约] 预约数据请求异常', friendlyMessage, error);
        
        // 记录友好的系统日志
        this.addSystemLog(friendlyMessage);
        
        // 保持当前预约数据状态，不清空现有数据
      },
  
      /**
       * 处理解析后的预约数据
       * @param {Array} appointmentArray - 解析后的预约数据数组
       * @param {Array} protectedVirtualData - 受保护的虚拟数据（治疗时间未结束的虚拟数据）
       */
      processAppointmentData(appointmentArray, protectedVirtualData = []) {
        // 关键修复：添加数据操作锁，防止竞态条件
        if (this.isProcessingAppointmentData) {
          logger.warn(`[数据处理] ⚠️ 跳过：正在处理数据中，避免竞态条件`);
          return;
        }
        
        this.isProcessingAppointmentData = true;
        
        try {
          logger.debug(`[数据处理] processAppointmentData调用`, {
            newArrayLength: appointmentArray?.length,
            existingCount: this.appointmentData?.length,
            currentPage: this.currentPage
          });
          
          // 关键检查：记录后端返回的原始数据，检查脑波值
          if (appointmentArray && appointmentArray.length > 0) {
            const backendDataCheck = appointmentArray.map(item => ({
              roomId: item.roomId,
              roomName: item.roomName,
              brainwaveValues: {
                delta: item.delta,
                theta: item.theta,
                alpha: item.alpha,
                beta: item.beta,
                gamma: item.gamma
              },
              hasZeroValues: (item.delta === 0 || item.delta === undefined || item.delta === null) &&
                            (item.theta === 0 || item.theta === undefined || item.theta === null) &&
                            (item.alpha === 0 || item.alpha === undefined || item.alpha === null) &&
                            (item.beta === 0 || item.beta === undefined || item.beta === null) &&
                            (item.gamma === 0 || item.gamma === undefined || item.gamma === null)
            }));
            
            const roomsWithZeroValues = backendDataCheck.filter(r => r.hasZeroValues);
            //     })),
            //     backendDataCheck
          }
          
          // API返回空数据时，不生成模拟预约记录，保持空状态
          if (appointmentArray && appointmentArray.length > 0) {
            // 关键修复：保存现有数据的气泡状态，用于后续合并
            // 使用浅拷贝数组，但确保对象引用用于合并
            const existingData = [...this.appointmentData];
            
            // 关键修复：在创建 existingData 之后立即记录快照，确保数据一致性
            // 使用深拷贝值，确保快照不受后续修改影响
            const existingDataSnapshot = existingData.map(app => ({
              roomId: app.roomId,
              roomName: app.roomName,
              // 关键：记录实际的值，而不是引用
              delta: app.delta,
              theta: app.theta,
              alpha: app.alpha,
              beta: app.beta,
              gamma: app.gamma,
              hasRealtimeArray: !!(app.realtimeDataArray && app.realtimeDataArray.length > 0),
              hasLastValid: !!app.lastValidBrainwaveData,
              lastReceiveTime: app.lastReceiveTime,
              currentDisplayIndex: app.currentDisplayIndex
            }));
            
            logger.debug(`[数据处理] existingData快照已记录`, {
              existingDataCount: existingData.length,
              existingDataSnapshotCount: existingDataSnapshot.length,
              existingDataSnapshot
            });
            
            const realData = Array.isArray(appointmentArray) 
              ? appointmentArray.map((item) => {
                  // 虚拟数据：后端 dataType=1 或历史无 roomId 的 mock
                  const isVirtualData = item.dataType === 1 || item.isMock || !item.roomId;
                  
                  // 为虚拟数据生成脑波数据
                  if (isVirtualData) {
                    // 关键修复：检查现有数据中是否有相同的虚拟数据且治疗时间未结束
                    let existingVirtualItem = null;
                    if (item.patientName) {
                      existingVirtualItem = existingData.find(existing => 
                        (existing.isMock || existing.dataType === 1 || !existing.roomId) && 
                        existing.patientName === item.patientName
                      );
                    }
                    
                    // 如果找到现有虚拟数据，检查其治疗状态
                    if (existingVirtualItem) {
                      const treatmentStatus = this.getTreatmentStatus(existingVirtualItem);
                      
                      // 如果治疗时间未结束，保留现有数据，不进行刷新
                      if (treatmentStatus === '治疗中' || treatmentStatus === '治疗未开始') {
                        //   {
                        
                        // 返回现有数据，但更新可能变化的其他字段（如脑波数据）
                        const virtualBrainwaveData = this.generateVirtualBrainwaveData(
                          item.patientName,
                          item.sleepState || item.realtimeSleepState || '浅睡',
                          item
                        );
                        
                        // 保留现有的 waveData
                        let waveDataToUse = {
                          delta: [],
                          theta: [],
                          alpha: [],
                          beta: [],
                          gamma: []
                        };
                        
                        if (existingVirtualItem.waveData) {
                          waveDataToUse = {
                            delta: existingVirtualItem.waveData.delta ? [...existingVirtualItem.waveData.delta.map(waveItem => ({ ...waveItem }))] : [],
                            theta: existingVirtualItem.waveData.theta ? [...existingVirtualItem.waveData.theta.map(waveItem => ({ ...waveItem }))] : [],
                            alpha: existingVirtualItem.waveData.alpha ? [...existingVirtualItem.waveData.alpha.map(waveItem => ({ ...waveItem }))] : [],
                            beta: existingVirtualItem.waveData.beta ? [...existingVirtualItem.waveData.beta.map(waveItem => ({ ...waveItem }))] : [],
                            gamma: existingVirtualItem.waveData.gamma ? [...existingVirtualItem.waveData.gamma.map(waveItem => ({ ...waveItem }))] : []
                          };
                        }
                        
                        // 关键修复：确保lastValidBrainwaveData存在
                        let lastValidBrainwaveDataToUse = existingVirtualItem.lastValidBrainwaveData;
                        if (!lastValidBrainwaveDataToUse || 
                            (!lastValidBrainwaveDataToUse.delta && !lastValidBrainwaveDataToUse.theta && 
                             !lastValidBrainwaveDataToUse.alpha && !lastValidBrainwaveDataToUse.beta && 
                             !lastValidBrainwaveDataToUse.gamma)) {
                          // 如果lastValidBrainwaveData不存在或无效，使用新生成的虚拟数据
                          lastValidBrainwaveDataToUse = {
                            delta: virtualBrainwaveData.delta,
                            theta: virtualBrainwaveData.theta,
                            alpha: virtualBrainwaveData.alpha,
                            beta: virtualBrainwaveData.beta,
                            gamma: virtualBrainwaveData.gamma,
                            timestamp: Date.now()
                          };
                        }
                        
                        // 关键修复：如果waveData为空，至少初始化一个数据点
                        const hasWaveData = waveDataToUse.delta.length > 0 || 
                                           waveDataToUse.theta.length > 0 || 
                                           waveDataToUse.alpha.length > 0 || 
                                           waveDataToUse.beta.length > 0 || 
                                           waveDataToUse.gamma.length > 0;
                        
                        // 创建虚拟数据对象
                        const protectedVirtualItem = {
                          ...existingVirtualItem,
                          ...item,
                          // 关键：保留现有的治疗时间
                          startTime: existingVirtualItem.startTime,
                          endTime: existingVirtualItem.endTime,
                          appointmentDate: existingVirtualItem.appointmentDate || item.appointmentDate,
                          // 更新脑波数据（保持动态生成）
                          delta: virtualBrainwaveData.delta,
                          theta: virtualBrainwaveData.theta,
                          alpha: virtualBrainwaveData.alpha,
                          beta: virtualBrainwaveData.beta,
                          gamma: virtualBrainwaveData.gamma,
                          waveData: waveDataToUse,
                          isMock: true,
                          hideWaveform: false,
                          treatmentStatus: treatmentStatus, // 使用计算出的治疗状态
                          lastSleepStateChange: existingVirtualItem.lastSleepStateChange || Date.now(),
                          // 关键修复：确保lastValidBrainwaveData存在
                          lastValidBrainwaveData: lastValidBrainwaveDataToUse
                        };
                        
                        // 如果waveData为空，立即填充初始数据点
                        if (!hasWaveData) {
                          protectedVirtualItem.waveData = {
                            delta: [],
                            theta: [],
                            alpha: [],
                            beta: [],
                            gamma: []
                          };
                          this.updateWaveData(protectedVirtualItem, {
                            delta: virtualBrainwaveData.delta / 100,
                            theta: virtualBrainwaveData.theta / 100,
                            alpha: virtualBrainwaveData.alpha / 100,
                            beta: virtualBrainwaveData.beta / 100,
                            gamma: virtualBrainwaveData.gamma / 100
                          });
                        }
                        
                        return protectedVirtualItem;
                      }
                      // 如果治疗时间已结束，允许更新（使用后端返回的新数据）
                    }
                    
                    // 如果没有现有数据或治疗时间已结束，正常处理虚拟数据
                    const virtualBrainwaveData = this.generateVirtualBrainwaveData(
                      item.patientName,
                      item.sleepState || item.realtimeSleepState || '浅睡',
                      item
                    );
                    
                    // 关键修复：尝试从 existingData 中恢复虚拟数据的 waveData
                    // 通过 patientName 或其他标识匹配（虚拟数据可能没有 roomId）
                    let existingVirtualItemForWaveData = null;
                    if (item.patientName) {
                      existingVirtualItemForWaveData = existingData.find(existing => 
                        (existing.isMock || existing.dataType === 1 || !existing.roomId) && 
                        existing.patientName === item.patientName &&
                        existing.waveData && (
                          (existing.waveData.delta && existing.waveData.delta.length > 0) ||
                          (existing.waveData.theta && existing.waveData.theta.length > 0) ||
                          (existing.waveData.alpha && existing.waveData.alpha.length > 0) ||
                          (existing.waveData.beta && existing.waveData.beta.length > 0) ||
                          (existing.waveData.gamma && existing.waveData.gamma.length > 0)
                        )
                      );
                    }
                    
                    // 如果有匹配的虚拟数据，恢复其 waveData
                    let waveDataToUse = {
                      delta: [],
                      theta: [],
                      alpha: [],
                      beta: [],
                      gamma: []
                    };
                    
                    if (existingVirtualItemForWaveData && existingVirtualItemForWaveData.waveData) {
                      waveDataToUse = {
                        delta: existingVirtualItemForWaveData.waveData.delta ? [...existingVirtualItemForWaveData.waveData.delta.map(waveItem => ({ ...waveItem }))] : [],
                        theta: existingVirtualItemForWaveData.waveData.theta ? [...existingVirtualItemForWaveData.waveData.theta.map(waveItem => ({ ...waveItem }))] : [],
                        alpha: existingVirtualItemForWaveData.waveData.alpha ? [...existingVirtualItemForWaveData.waveData.alpha.map(waveItem => ({ ...waveItem }))] : [],
                        beta: existingVirtualItemForWaveData.waveData.beta ? [...existingVirtualItemForWaveData.waveData.beta.map(waveItem => ({ ...waveItem }))] : [],
                        gamma: existingVirtualItemForWaveData.waveData.gamma ? [...existingVirtualItemForWaveData.waveData.gamma.map(waveItem => ({ ...waveItem }))] : []
                      };
                    }
                    
                    // 关键修复：创建虚拟数据对象
                    const virtualItem = {
                      ...item,
                      delta: virtualBrainwaveData.delta,
                      theta: virtualBrainwaveData.theta,
                      alpha: virtualBrainwaveData.alpha,
                      beta: virtualBrainwaveData.beta,
                      gamma: virtualBrainwaveData.gamma,
                      waveData: waveDataToUse,
                      isMock: true,
                      hideWaveform: false, // 确保虚拟数据显示波形图
                      treatmentStatus: '治疗中', // 确保虚拟数据显示为治疗中状态
                      lastSleepStateChange: Date.now(), // 初始化睡眠状态切换时间
                      // 关键修复：立即设置lastValidBrainwaveData
                      lastValidBrainwaveData: {
                        delta: virtualBrainwaveData.delta,
                        theta: virtualBrainwaveData.theta,
                        alpha: virtualBrainwaveData.alpha,
                        beta: virtualBrainwaveData.beta,
                        gamma: virtualBrainwaveData.gamma,
                        timestamp: Date.now()
                      }
                    };
                    
                    // 关键修复：如果waveData为空，立即调用updateWaveData填充初始数据点
                    const hasWaveData = waveDataToUse.delta.length > 0 || 
                                       waveDataToUse.theta.length > 0 || 
                                       waveDataToUse.alpha.length > 0 || 
                                       waveDataToUse.beta.length > 0 || 
                                       waveDataToUse.gamma.length > 0;
                    
                    if (!hasWaveData) {
                      // 初始化waveData为空对象，让updateWaveData填充
                      virtualItem.waveData = {
                        delta: [],
                        theta: [],
                        alpha: [],
                        beta: [],
                        gamma: []
                      };
                      // 调用updateWaveData填充初始数据点
                      this.updateWaveData(virtualItem, {
                        delta: virtualBrainwaveData.delta / 100,
                        theta: virtualBrainwaveData.theta / 100,
                        alpha: virtualBrainwaveData.alpha / 100,
                        beta: virtualBrainwaveData.beta / 100,
                        gamma: virtualBrainwaveData.gamma / 100
                      });
                    }
                    
                    return virtualItem;
                  } else {
                    // 真实数据：初始化脑波值，确保不会因为 undefined 导致问题
                    // 关键：不要将脑波值初始化为0，保留为undefined，让合并逻辑和实时数据更新逻辑处理
                    const initializedItem = {
                      ...item,
                      isMock: false,
                    };
                    
                    // 关键修复：强化waveData保留逻辑，确保在任何情况下都不会丢失历史数据
                    // 1. 首先尝试精确匹配roomId
                    let existingItemForWaveData = existingData.find(existing => 
                      existing.roomId && initializedItem.roomId && 
                      existing.roomId.toString() === initializedItem.roomId.toString()
                    );
                    
                    // 2. 如果精确匹配失败，在整个existingData中搜索任何有相同roomId且有waveData的项
                    if (!existingItemForWaveData || !existingItemForWaveData.waveData) {
                      existingItemForWaveData = existingData.find(existing => {
                        if (!existing.roomId || !initializedItem.roomId) return false;
                        const match = existing.roomId.toString() === initializedItem.roomId.toString();
                        return match && existing.waveData && (
                          (existing.waveData.delta && existing.waveData.delta.length > 0) ||
                          (existing.waveData.theta && existing.waveData.theta.length > 0) ||
                          (existing.waveData.alpha && existing.waveData.alpha.length > 0) ||
                          (existing.waveData.beta && existing.waveData.beta.length > 0) ||
                          (existing.waveData.gamma && existing.waveData.gamma.length > 0)
                        );
                      });
                    }
                    
                    if (existingItemForWaveData && existingItemForWaveData.waveData) {
                      // 旧数据有 waveData，立即深拷贝保留
                      initializedItem.waveData = {
                        delta: existingItemForWaveData.waveData.delta ? [...existingItemForWaveData.waveData.delta.map(item => ({ ...item }))] : [],
                        theta: existingItemForWaveData.waveData.theta ? [...existingItemForWaveData.waveData.theta.map(item => ({ ...item }))] : [],
                        alpha: existingItemForWaveData.waveData.alpha ? [...existingItemForWaveData.waveData.alpha.map(item => ({ ...item }))] : [],
                        beta: existingItemForWaveData.waveData.beta ? [...existingItemForWaveData.waveData.beta.map(item => ({ ...item }))] : [],
                        gamma: existingItemForWaveData.waveData.gamma ? [...existingItemForWaveData.waveData.gamma.map(item => ({ ...item }))] : []
                      };
                      logger.debug(`[初始化] ✓ 房间 ${initializedItem.roomId}: 从旧数据成功复制了 waveData`, {
                        roomId: initializedItem.roomId,
                        waveDataLengths: {
                          delta: initializedItem.waveData.delta.length,
                          theta: initializedItem.waveData.theta.length,
                          alpha: initializedItem.waveData.alpha.length,
                          beta: initializedItem.waveData.beta.length,
                          gamma: initializedItem.waveData.gamma.length
                        },
                        source: 'existingData'
                      });
                    } else if (!initializedItem.waveData) {
                      // 如果旧数据没有 waveData，初始化空数组
                      // 这是最后的选择，只有在确实找不到任何历史数据时才初始化空数组
                      initializedItem.waveData = {
                        delta: [],
                        theta: [],
                        alpha: [],
                        beta: [],
                        gamma: []
                      };
                      logger.debug(`[初始化] ⚠️ 房间 ${initializedItem.roomId}: 未找到旧waveData，初始化为空数组`, {
                        roomId: initializedItem.roomId,
                        existingDataCount: existingData.length,
                        searchedRoomIds: existingData.map(e => e.roomId).filter(Boolean)
                      });
                    }
                    
                    // 重要：不要在这里初始化脑波值为0
                    // 如果脑波值未定义，保持undefined，不要设为0
                    // 合并逻辑会从现有数据中恢复，或者等待实时数据更新
                    // 这样可以避免在轮播切换时意外重置为0
                    
                    // 关键修复：如果后端返回0值，检查旧数据是否有有效值
                    // 如果旧值有效，将新值改为undefined，让合并逻辑处理
                    const brainwaveBands = ['delta', 'theta', 'alpha', 'beta', 'gamma'];
                    
                    // 查找旧数据中对应的项（如果存在）
                    const existingItemForInit = existingData.find(existing => 
                      existing.roomId && initializedItem.roomId && 
                      existing.roomId.toString() === initializedItem.roomId.toString()
                    );
                    
                    const filteredValues = [];
                    brainwaveBands.forEach(band => {
                      const newValue = initializedItem[band];
                      const existingValue = existingItemForInit ? existingItemForInit[band] : undefined;
                      
                      // 如果新值是0，且旧值存在且有效（非0且非undefined），将其改为undefined
                      // 这样合并逻辑会正确保留旧值
                      if (newValue === 0 || newValue === null) {
                        if (existingValue !== undefined && existingValue !== null && existingValue !== 0) {
                          // 旧值有效，将新值改为undefined，让合并逻辑处理
                          initializedItem[band] = undefined;
                          filteredValues.push({
                            band,
                            reason: '后端返回0值，但旧值有效，改为undefined',
                            newValue: 0,
                            existingValue
                          });
                        } else {
                          // 旧值也无效或不存在，保持新值（可能是0）
                          // 但记录日志
                          filteredValues.push({
                            band,
                            reason: '后端返回0值，旧值也无效，保持0',
                            newValue: 0,
                            existingValue
                          });
                        }
                      } else if (newValue === undefined) {
                        // 新值未定义，保持undefined，让合并逻辑处理
                        filteredValues.push({
                          band,
                          reason: '新值undefined，保持undefined',
                          newValue: undefined,
                          existingValue
                        });
                      } else if (newValue > 0) {
                        // 新值有效，保持新值
                        filteredValues.push({
                          band,
                          reason: '新值有效，保持新值',
                          newValue,
                          existingValue
                        });
                      }
                    });
                    
                    // 记录初始化过滤的日志
                    if (filteredValues.some(v => v.reason.includes('改为undefined'))) {
                      logger.debug(`[初始化过滤] 房间 ${initializedItem.roomId}: 过滤了后端返回的0值`, {
                        roomId: initializedItem.roomId,
                        filteredValues
                      });
                    }
                    
                    // 真实数据不生成虚拟脑波，仅由实时接口通过 realtimeDataArray/waveData 提供脑波
                    return initializedItem;
                  }
                }).filter(item => item !== null) // 过滤掉被跳过的虚拟数据（null值）
              : [];
            
            // 验证并修正预约状态（基于新增的appointmentDate字段）
            const correctedData = this.validateAndCorrectAppointmentStatus(realData);
            
            
            let newData = this.markNewAppointments(correctedData);
  
            if (newData.length > 0) {
              // 初始化新数据的气泡数组和上次生成时间
              this.initBubbleData(newData);
              // #region agent log
              newData.forEach((item, idx) => {
                if (item && item.roomId) {
  
                }
              });
              // #endregion
              
              // 关键调试：记录合并前的新数据状态
              const newDataBeforeMerge = newData.map(item => ({
                roomId: item.roomId,
                roomName: item.roomName,
                delta: item.delta,
                theta: item.theta,
                alpha: item.alpha,
                beta: item.beta,
                gamma: item.gamma,
                hasRealtimeArray: !!(item.realtimeDataArray && item.realtimeDataArray.length > 0),
                hasLastValid: !!item.lastValidBrainwaveData
              }));
              logger.debug(`[数据处理] 合并前的新数据状态`, {
                newDataBeforeMerge
              });
              
              // 关键：合并现有数据的气泡状态和脑波数据（在赋值前执行）
              // 这确保了所有脑波相关数据（包括 realtimeDataArray）都被正确保留
              this.mergeExistingBubbleData(existingData, newData);
              
              // 虚拟数据条数上限（与后端一致）：超出时优先保留「治疗中」
              const MAX_VIRTUAL_APPOINTMENTS = 4;
              const virtualDataInNewData = newData.filter(item => 
                item && (item.isMock || item.dataType === 1 || !item.roomId)
              );
              
              if (virtualDataInNewData.length > MAX_VIRTUAL_APPOINTMENTS) {
                const byPriority = [...virtualDataInNewData].sort((a, b) => {
                  const pa = this.getTreatmentStatus(a) === '治疗中' ? 0 : 1;
                  const pb = this.getTreatmentStatus(b) === '治疗中' ? 0 : 1;
                  if (pa !== pb) return pa - pb;
                  return 0;
                });
                const keepSet = new Set(byPriority.slice(0, MAX_VIRTUAL_APPOINTMENTS));
                logger.debug(`[虚拟数据条数控制] 检测到 ${virtualDataInNewData.length} 个虚拟数据，保留 ${MAX_VIRTUAL_APPOINTMENTS} 条`, {
                  totalVirtual: virtualDataInNewData.length,
                  kept: [...keepSet].map(i => i.patientName)
                });
                newData = newData.filter(item => {
                  if (!item) return false;
                  const isVirtual = item.isMock || item.dataType === 1 || !item.roomId;
                  if (isVirtual) {
                    return keepSet.has(item);
                  }
                  return true;
                });
              }
              
              // 关键修复：保护虚拟数据的治疗时间不被覆盖
              // 对于治疗时间未结束的虚拟数据，保留其 startTime 和 endTime
              newData.forEach(newItem => {
                // 检查是否为虚拟数据
                const isVirtualData = newItem.isMock || newItem.dataType === 1 || !newItem.roomId;
                
                if (isVirtualData && newItem.patientName) {
                  // 查找现有数据中相同的虚拟数据
                  const existingVirtualItem = existingData.find(existing => 
                    (existing.isMock || existing.dataType === 1 || !existing.roomId) && 
                    existing.patientName === newItem.patientName
                  );
                  
                  if (existingVirtualItem) {
                    const treatmentStatus = this.getTreatmentStatus(existingVirtualItem);
                    
                    // 如果治疗时间未结束，保护其治疗时间字段
                    if (treatmentStatus === '治疗中' || treatmentStatus === '治疗未开始') {
                      // 保留现有的治疗时间
                      if (existingVirtualItem.startTime) {
                        newItem.startTime = existingVirtualItem.startTime;
                      }
                      if (existingVirtualItem.endTime) {
                        newItem.endTime = existingVirtualItem.endTime;
                      }
                      if (existingVirtualItem.appointmentDate) {
                        newItem.appointmentDate = existingVirtualItem.appointmentDate;
                      }
                      
                    }
                  }
                }
              });
              
              // 关键调试：记录合并后的最终数据状态
              const mergedDataSnapshot = newData.map(item => ({
                roomId: item.roomId,
                roomName: item.roomName,
                delta: item.delta,
                theta: item.theta,
                alpha: item.alpha,
                beta: item.beta,
                gamma: item.gamma,
                hasRealtimeArray: !!(item.realtimeDataArray && item.realtimeDataArray.length > 0),
                hasLastValid: !!item.lastValidBrainwaveData,
                lastReceiveTime: item.lastReceiveTime,
                currentDisplayIndex: item.currentDisplayIndex
              }));
              
              // 关键修复：对比合并前后的数据，找出变成0的值
              // 增强检测逻辑，添加详细的调试信息
              const zeroChanges = [];
              const unmatchedExisting = [];
              const unmatchedMerged = [];
              
              existingDataSnapshot.forEach((existing, idx) => {
                const merged = mergedDataSnapshot.find(m => m.roomId === existing.roomId);
                if (merged) {
                  const bands = ['delta', 'theta', 'alpha', 'beta', 'gamma'];
                  bands.forEach(band => {
                    const existingValue = existing[band];
                    const mergedValue = merged[band];
                    
                    // 检测：旧值有效（非0且非undefined），但合并后无效（0或undefined）
                    if (existingValue !== undefined && existingValue !== null && existingValue !== 0 && 
                        (mergedValue === undefined || mergedValue === null || mergedValue === 0)) {
                      // 关键修复：从 existingDataSnapshot 中获取恢复值（合并前的实际值）
                      zeroChanges.push({
                        roomId: existing.roomId,
                        roomName: existing.roomName,
                        band,
                        before: existingValue,
                        after: mergedValue,
                        snapshotValue: existingValue, // 从快照中获取，这是合并前的实际值
                        reason: '合并后变成0或undefined'
                      });
                    }
                  });
                } else {
                  // 在 existingDataSnapshot 中但在 mergedDataSnapshot 中找不到匹配项
                  unmatchedExisting.push({
                    roomId: existing.roomId,
                    roomName: existing.roomName,
                    values: {
                      delta: existing.delta,
                      theta: existing.theta,
                      alpha: existing.alpha,
                      beta: existing.beta,
                      gamma: existing.gamma
                    }
                  });
                }
              });
              
              // 检查 mergedDataSnapshot 中是否有新项（在 existingDataSnapshot 中不存在）
              mergedDataSnapshot.forEach(merged => {
                const existing = existingDataSnapshot.find(e => e.roomId === merged.roomId);
                if (!existing) {
                  unmatchedMerged.push({
                    roomId: merged.roomId,
                    roomName: merged.roomName,
                    values: {
                      delta: merged.delta,
                      theta: merged.theta,
                      alpha: merged.alpha,
                      beta: merged.beta,
                      gamma: merged.gamma
                    }
                  });
                }
              });
              
              if (zeroChanges.length > 0) {
                logger.error(`[数据处理] ⚠️ 合并后数据丢失！`, {
                  zeroChangesCount: zeroChanges.length,
                  zeroChanges: zeroChanges.map(z => ({
                    roomId: z.roomId,
                    roomName: z.roomName,
                    band: z.band,
                    before: z.before,
                    after: z.after,
                    existingItemValue: z.existingItemValue,
                    hasLastValid: z.hasLastValid,
                    reason: z.reason
                  })),
                  unmatchedExisting,
                  unmatchedMerged,
                  existingDataSnapshot: existingDataSnapshot.map(e => ({
                    roomId: e.roomId,
                    roomName: e.roomName,
                    values: {
                      delta: e.delta,
                      theta: e.theta,
                      alpha: e.alpha,
                      beta: e.beta,
                      gamma: e.gamma
                    }
                  })),
                  mergedDataSnapshot: mergedDataSnapshot.map(m => ({
                    roomId: m.roomId,
                    roomName: m.roomName,
                    values: {
                      delta: m.delta,
                      theta: m.theta,
                      alpha: m.alpha,
                      beta: m.beta,
                      gamma: m.gamma
                    }
                  }))
                });
                
                // 关键修复：立即尝试恢复丢失的值
                // 从 existingDataSnapshot 中恢复，因为快照记录的是合并前的实际值
                const recoveryResults = [];
                zeroChanges.forEach(zeroChange => {
                  // 在 newData 中找到对应的项
                  const targetItem = newData.find(item => 
                    item.roomId && zeroChange.roomId && 
                    item.roomId.toString() === zeroChange.roomId.toString()
                  );
                  
                  if (targetItem) {
                    // 关键修复：从 existingDataSnapshot 中获取恢复值
                    // existingDataSnapshot 记录的是合并前的实际值，不受后续修改影响
                    const snapshotItem = existingDataSnapshot.find(item => 
                      item.roomId && zeroChange.roomId && 
                      item.roomId.toString() === zeroChange.roomId.toString()
                    );
                    
                    if (snapshotItem) {
                      // 从快照中获取恢复值（这是合并前的实际值）
                      const recoveryValue = zeroChange.snapshotValue || snapshotItem[zeroChange.band];
                      
                      // 如果 recoveryValue 有效，进行恢复
                      if (recoveryValue !== undefined && recoveryValue !== null && recoveryValue !== 0) {
                        const beforeRecovery = targetItem[zeroChange.band];
                        targetItem[zeroChange.band] = recoveryValue;
                        recoveryResults.push({
                          roomId: zeroChange.roomId,
                          roomName: zeroChange.roomName,
                          band: zeroChange.band,
                          beforeRecovery: beforeRecovery,
                          afterRecovery: recoveryValue,
                          source: 'existingDataSnapshot'
                        });
                      } else {
                        // 如果快照中的值也无效，尝试从 existingData 中查找 lastValidBrainwaveData
                        const sourceItem = existingData.find(item => 
                          item.roomId && zeroChange.roomId && 
                          item.roomId.toString() === zeroChange.roomId.toString()
                        );
                        
                        if (sourceItem && sourceItem.lastValidBrainwaveData) {
                          // 尝试从 lastValidBrainwaveData 恢复
                          const lastValidValue = sourceItem.lastValidBrainwaveData[zeroChange.band];
                          if (lastValidValue !== undefined && lastValidValue !== null && lastValidValue !== 0) {
                            const beforeRecovery = targetItem[zeroChange.band];
                            targetItem[zeroChange.band] = lastValidValue;
                            recoveryResults.push({
                              roomId: zeroChange.roomId,
                              roomName: zeroChange.roomName,
                              band: zeroChange.band,
                              beforeRecovery: beforeRecovery,
                              afterRecovery: lastValidValue,
                              source: 'lastValidBrainwaveData'
                            });
                          }
                        }
                      }
                    }
                  }
                });
                
                if (recoveryResults.length > 0) {
                  logger.warn(`[数据处理] ✓ 已恢复 ${recoveryResults.length} 个丢失的值`, {
                    recoveryResults
                  });
                } else {
                  logger.error(`[数据处理] ✗ 无法恢复丢失的值`, {
                    zeroChangesCount: zeroChanges.length
                  });
                }
              } else {
              }
              
              // 只有在合并完成后才赋值，确保所有数据都被保留
              logger.debug(`[数据处理] 准备赋值 appointmentData，数据量: ${newData.length}`);
              
              // 关键调试：在赋值前为每个appointment设置零值追踪器
              const brainwaveBands = ['delta', 'theta', 'alpha', 'beta', 'gamma'];
              newData.forEach((appointment) => {
                if (appointment && appointment.roomId) {
                  brainwaveBands.forEach(band => {
                    let currentValue = appointment[band];
                    try {
                      Object.defineProperty(appointment, band, {
                        get: function() {
                          return currentValue;
                        },
                        set: function(newValue) {
                          // 如果值从非0变成0，记录详细信息
                          if (currentValue !== undefined && currentValue !== 0 && 
                              (newValue === undefined || newValue === 0)) {
                            const stack = new Error().stack;
                            logger.error(`[零值追踪] ⚠️ ${band} 从 ${currentValue} 变成 ${newValue}`, {
                              roomId: appointment.roomId,
                              roomName: appointment.roomName,
                              band,
                              before: currentValue,
                              after: newValue,
                              stack: stack?.split('\n').slice(0, 10).join('\n')
                            });
                          }
                          currentValue = newValue;
                        },
                        configurable: true,
                        enumerable: true
                      });
                    } catch (e) {
                      // 如果无法设置拦截器（可能已经设置过），忽略错误
                    }
                  });
                }
              });
              
              // 关键修复4：在赋值前验证waveData完整性，确保历史数据不丢失
              // 遍历newData，检查每个项的waveData，如果为空但existingData中有数据，进行恢复
              const waveDataRecoveries = [];
              newData.forEach(newItem => {
                if (!newItem.roomId) return;
                
                // 检查newItem的waveData是否为空
                const hasWaveData = newItem.waveData && (
                  (newItem.waveData.delta && newItem.waveData.delta.length > 0) ||
                  (newItem.waveData.theta && newItem.waveData.theta.length > 0) ||
                  (newItem.waveData.alpha && newItem.waveData.alpha.length > 0) ||
                  (newItem.waveData.beta && newItem.waveData.beta.length > 0) ||
                  (newItem.waveData.gamma && newItem.waveData.gamma.length > 0)
                );
                
                // 如果waveData为空，尝试从existingData恢复
                if (!hasWaveData) {
                  const sourceItem = existingData.find(existing => 
                    existing.roomId && newItem.roomId &&
                    existing.roomId.toString() === newItem.roomId.toString() &&
                    existing.waveData && (
                      (existing.waveData.delta && existing.waveData.delta.length > 0) ||
                      (existing.waveData.theta && existing.waveData.theta.length > 0) ||
                      (existing.waveData.alpha && existing.waveData.alpha.length > 0) ||
                      (existing.waveData.beta && existing.waveData.beta.length > 0) ||
                      (existing.waveData.gamma && existing.waveData.gamma.length > 0)
                    )
                  );
                  
                  if (sourceItem && sourceItem.waveData) {
                    // 从sourceItem恢复waveData（深拷贝）
                    newItem.waveData = {
                      delta: sourceItem.waveData.delta ? [...sourceItem.waveData.delta.map(d => ({ ...d }))] : [],
                      theta: sourceItem.waveData.theta ? [...sourceItem.waveData.theta.map(d => ({ ...d }))] : [],
                      alpha: sourceItem.waveData.alpha ? [...sourceItem.waveData.alpha.map(d => ({ ...d }))] : [],
                      beta: sourceItem.waveData.beta ? [...sourceItem.waveData.beta.map(d => ({ ...d }))] : [],
                      gamma: sourceItem.waveData.gamma ? [...sourceItem.waveData.gamma.map(d => ({ ...d }))] : []
                    };
                    
                    waveDataRecoveries.push({
                      roomId: newItem.roomId,
                      roomName: newItem.roomName,
                      waveDataLengths: {
                        delta: newItem.waveData.delta.length,
                        theta: newItem.waveData.theta.length,
                        alpha: newItem.waveData.alpha.length,
                        beta: newItem.waveData.beta.length,
                        gamma: newItem.waveData.gamma.length
                      }
                    });
                  }
                }
              });
              
              if (waveDataRecoveries.length > 0) {
                logger.debug(`[赋值前验证] ✓ 已恢复 ${waveDataRecoveries.length} 个项的waveData`, {
                  recoveries: waveDataRecoveries
                });
              }
              
              // 合并受保护的虚拟数据：若请求异常导致某条未返回，用本地未结束疗程的副本补回（最多 4 条）
              if (protectedVirtualData && protectedVirtualData.length > 0) {
                const isVirt = (x) => x && (x.isMock || x.dataType === 1 || !x.roomId);
                const matchesProtected = (n, p) => isVirt(n) &&
                  n.patientName === p.patientName &&
                  n.startTime === p.startTime &&
                  n.endTime === p.endTime;
                let virtualCount = newData.filter(isVirt).length;
                for (const pv of protectedVirtualData) {
                  const ts = this.getTreatmentStatus(pv);
                  if (ts !== '治疗中' && ts !== '治疗未开始') continue;
                  const already = newData.some(n => matchesProtected(n, pv));
                  if (!already && virtualCount < MAX_VIRTUAL_APPOINTMENTS) {
                    newData.push(pv);
                    virtualCount += 1;
                  }
                }
              }

              // 统一字段回填：确保刷新后不会丢失脑波渲染关键状态
              const preservedFields = [
                'waveData',
                'realtimeDataArray',
                'lastValidBrainwaveData',
                'currentDisplayIndex',
                'lastReceiveTime'
              ];
              const oldDataByRoomId = new Map();
              (existingData || []).forEach((oldItem) => {
                if (!oldItem || !oldItem.roomId) return;
                oldDataByRoomId.set(String(oldItem.roomId), oldItem);
              });
              (newData || []).forEach((newItem) => {
                if (!newItem || !newItem.roomId) return;
                const oldItem = oldDataByRoomId.get(String(newItem.roomId));
                if (!oldItem) return;
                preservedFields.forEach((field) => {
                  const newValue = newItem[field];
                  const oldValue = oldItem[field];
                  const missingArray = Array.isArray(oldValue) && (!Array.isArray(newValue) || newValue.length === 0);
                  const missingObject = oldValue && typeof oldValue === 'object' && !Array.isArray(oldValue) &&
                    (!newValue || (typeof newValue === 'object' && Object.keys(newValue).length === 0));
                  const missingScalar = (newValue === undefined || newValue === null) && (oldValue !== undefined && oldValue !== null);
                  if (missingArray || missingObject || missingScalar) {
                    if (field === 'waveData' && oldValue) {
                      newItem[field] = {
                        delta: Array.isArray(oldValue.delta) ? [...oldValue.delta.map(d => ({ ...d }))] : [],
                        theta: Array.isArray(oldValue.theta) ? [...oldValue.theta.map(d => ({ ...d }))] : [],
                        alpha: Array.isArray(oldValue.alpha) ? [...oldValue.alpha.map(d => ({ ...d }))] : [],
                        beta: Array.isArray(oldValue.beta) ? [...oldValue.beta.map(d => ({ ...d }))] : [],
                        gamma: Array.isArray(oldValue.gamma) ? [...oldValue.gamma.map(d => ({ ...d }))] : []
                      };
                    } else if (field === 'realtimeDataArray' && Array.isArray(oldValue)) {
                      newItem[field] = oldValue.map(d => ({ ...d }));
                    } else if (oldValue && typeof oldValue === 'object') {
                      newItem[field] = { ...oldValue };
                    } else {
                      newItem[field] = oldValue;
                    }
                  }
                });
              });
              
              this.appointmentData = newData;
              
              // 关键修复：在赋值后立即调用updateVirtualBrainwaveData，确保虚拟数据持续更新
              // 这样可以确保虚拟数据的脑波值、waveData和lastValidBrainwaveData都能持续更新
              this.updateVirtualBrainwaveData();
              
              // 关键调试：记录赋值后的数据状态
              const afterAssignmentSnapshot = this.appointmentData.map(app => ({
                roomId: app.roomId,
                roomName: app.roomName,
                delta: app.delta,
                theta: app.theta,
                alpha: app.alpha,
                beta: app.beta,
                gamma: app.gamma,
                hasRealtimeArray: !!(app.realtimeDataArray && app.realtimeDataArray.length > 0),
                hasLastValid: !!app.lastValidBrainwaveData
              }));
              logger.debug(`[数据处理] 赋值后的数据状态`, {
                afterAssignmentSnapshot
              });
              // })));
              this.addSystemLog('成功获取预约数据');
            } else {
              // 没有有效数据时清空数组
              logger.debug(`[数据赋值] 清空appointmentData: API返回数据处理后为空`);
              this.appointmentData = [];
              this.addSystemLog('API返回数据处理后为空');
            }
  
            this.$nextTick(() => {
              this.updateRotation();
            });
          } else {
            // 没有数据时清空数组，不生成模拟预约记录
            logger.debug(`[数据赋值] 清空appointmentData: 没有预约数据`);
            this.appointmentData = [];
            this.addSystemLog('没有预约数据');
          }
        } catch (error) {
          logger.debug(`[数据赋值] 清空appointmentData: 处理异常 - ${error.message || '未知错误'}`);
          this.addSystemLog('处理预约数据异常: ' + (error.message || '未知错误'));
          // 获取数据失败时清空数组，不生成模拟预约记录
          this.appointmentData = [];
        } finally {
          // 关键修复：释放数据操作锁
          this.isProcessingAppointmentData = false;
        }
      },
      
      // 验证并修正预约状态（基于appointmentDate字段）
      validateAndCorrectAppointmentStatus: function(appointmentData) {
        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0); // 设置为今天的开始时间（本地时间）
        let correctedCount = 0;
        
        
        const correctedData = appointmentData.map((appointment) => {
          if (!appointment) {
            return appointment;
          }
          
          // 优先使用新增的appointmentDate字段
          let appointmentDate = null;
          if (appointment.appointmentDate) {
            appointmentDate = new Date(appointment.appointmentDate);
            appointmentDate.setHours(0, 0, 0, 0); // 设置为预约日期的开始时间（本地时间）
          } else if (appointment.startTime) {
            // 如果没有appointmentDate字段，则从startTime中提取日期
            const startTime = this.parseTimeString(appointment.startTime);
            if (startTime) {
              appointmentDate = new Date(startTime);
              appointmentDate.setHours(0, 0, 0, 0);
            }
          }
          
          
          // 如果预约日期早于今天，则标记为已结束
          if (appointmentDate && appointmentDate < today) {
            // 检查当前状态是否不是"已结束"
            if (appointment.treatmentStatus !== '已结束') {
              correctedCount++;
              this.addSystemLog('修正历史预约状态：' + appointment.roomName + ' - ' + appointment.patientName + ' (预约日期：' + (appointment.appointmentDate || appointmentDate.toISOString().split('T')[0]) + ')');
            }
            
            // 确保历史预约状态标记为已结束
            const correctedAppointment = Object.assign({}, appointment, {
              treatmentStatus: '已结束',
              isHistorical: true // 添加历史标记
            });
            return correctedAppointment;
          }
           return appointment;
        });
        
        if (correctedCount > 0) {
          this.addSystemLog('共修正了 ' + correctedCount + ' 个历史预约的治疗状态（基于appointmentDate字段）');
        }
        
        return correctedData;
      },
      
    }
  };
}
